import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ACCEPTABLE_FILE_MIME_TYPES, TFileType } from '@partners/utils';
import { BaseCustomControl } from '../custom-control';

@Component({
  selector: 'file-upload',
  standalone: true,
  template: `
    <div class="upload-trigger" (click)="triggerUpload()" [class.disabled]="disabled()">
      <ng-content></ng-content>
    </div>
    <input
      #fileInput
      [accept]="fileInputMimeTypesAcceptString()"
      type="file"
      [multiple]="multiple()"
      (change)="onFileSelected($event)"
      style="display: none;"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadComponent extends BaseCustomControl<File | File[]> {
  //force only one file to be uiploaded by default.
  multiple = input<boolean>(false);
  //Invoke change event even if the same file is uploaded again
  shouldNotifyOnEveryUpload = input<boolean>(false);
  //what kind of files can be uploaded
  acceptableMimeTypes = input<TFileType[]>(ACCEPTABLE_FILE_MIME_TYPES);

  fileInputMimeTypesAcceptString = computed(() => {
    const res = this.acceptableMimeTypes().reduce((str, amt, index) => `${str}${index ? ', ' : ''}${amt}`, '');

    return res;
  });

  @ViewChild('fileInput')
  fileInput!: ElementRef<HTMLInputElement>;

  selectedFiles: File[] = [];

  triggerUpload() {
    if (!this.disabled()) {
      this.fileInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      // Convert FileList to an Array
      this.selectedFiles = Array.from(input.files);

      // Emit value to the form (either array or single file based on need)
      this.writeValue(this.selectedFiles);
      this.onChange(this.selectedFiles);
      this.onTouched();

      //we must clear the input value to allow re-uploading the same file if needed for example
      //if the user uploaded a file but then removed it and wants to upload it again or
      //in case we use the change event to trigger some action like uploading the file immediately
      if (this.shouldNotifyOnEveryUpload()) {
        input.value = null;
      }
    }
  }

  override writeValue(value: any): void {
    // We cannot set the value of a file input programmatically for security reasons.
    // However, we can clear it if null is passed.
    if (!value && this.fileInput) {
      this.fileInput.nativeElement.value = '';
      this.selectedFiles = [];
    }
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
  }

  constructor() {
    super();
  }
}
